import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaDownload,
  FaTimes,
  FaEye,
  FaBoxOpen,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  useNavigate,
} from "react-router-dom";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const API_BASE =
  "https://nissi-india.com/api";

const PurchaseReqEnquiryReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const navigate =
    useNavigate();

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState(
    new Date()
      .toISOString()
      .split("T")[0]
  );

  const [
    reqNo,
    setReqNo,
  ] = useState("");

  const [
    store,
    setStore,
  ] = useState("");

  /* =========================================================
     DATA
  ========================================================= */

  const [
    storeOptions,
    setStoreOptions,
  ] = useState([]);

  const [
    results,
    setResults,
  ] = useState([]);

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "";

  const branch =
    (
      localStorage.getItem(
        "branchType"
      ) || ""
    ).toUpperCase();

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branch,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branch,
        userId,
      ]
    );

  /* =========================================================
     TEMPLATE DESIGN CLASSES
     SAME DESIGN STYLE AS FIRST PAGE
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     FORMAT DATE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const formatDate = (
    dateString
  ) => {
    if (!dateString) {
      return "-";
    }

    const date =
      new Date(
        dateString
      );

    return date.toLocaleDateString(
      "en-GB"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setFromDate("");

    setToDate(
      new Date()
        .toISOString()
        .split("T")[0]
    );

    setReqNo("");

    setStore("");

    setResults([]);
  };

  /* =========================================================
     VIEW
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleView = (
    item
  ) => {
    const id =
      item?.fld_enquiryid ||
      item?.fld_purchaseenquiryid;

    if (!id) {
      toast.warn(
        "Invalid enquiry id"
      );

      return;
    }

    navigate(
      `/inventory/PurchaseReqEnquiryReport_View?id=${id}`
    );
  };

  /* =========================================================
     PRINT SINGLE ROW
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = (
    item
  ) => {
    const id =
      item?.fld_enquiryid ||
      item?.fld_purchaseenquiryid;

    if (!id) {
      toast.warn(
        "Invalid enquiry id"
      );

      return;
    }

    window.open(
      `/inventory/PurchaseReqEnquiryReport_Print?id=${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /* =========================================================
     EXPORT
     ORIGINAL SECOND PAGE HAS NO EXPORT HANDLER/API
     DO NOT INVENT NEW BACKEND FUNCTIONALITY
  ========================================================= */

  const handleExport = () => {
    toast.info(
      "Export functionality is not connected in the existing Purchase Requisition Enquiry Report."
    );
  };

  /* =========================================================
     LOAD BRANCH / STORE OPTIONS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    const fetchStoreOptions =
      async () => {
        try {
          const res =
            await axios.post(
              `${API_BASE}/getFromStoreOptions`,
              {
                branchtype:
                  branch ||
                  "HO",

                branchid:
                  branchId ||
                  8,
              },
              {
                headers:
                  commonHeaders,
              }
            );

          setStoreOptions(
            res.data
              .storeOptions ||
              []
          );
        } catch (
          error
        ) {
          console.error(
            "Branch options fetch failed",
            error
          );

          toast.error(
            "Failed to load branch options"
          );
        }
      };

    fetchStoreOptions();
  }, [
    branch,
    branchId,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    async () => {
      if (!store) {
        toast.warn(
          "Please select a branch"
        );

        return;
      }

      const params = {
        branchid:
          store ||
          branchId ||
          "8",

        branchwiseno:
          reqNo ||
          "",

        fromdate:
          fromDate ||
          "",

        todate:
          toDate ||
          "",
      };

      try {
        setLoading(
          true
        );

        const res =
          await axios.get(
            `${API_BASE}/getenquirydetails_rpt`,
            {
              params,

              headers:
                commonHeaders,
            }
          );

        const data =
          Array.isArray(
            res.data?.data
          )
            ? res.data.data
            : [];

        setResults(
          data
        );

        toast.success(
          `Loaded ${data.length} record(s)`
        );
      } catch (
        err
      ) {
        console.error(
          "Enquiry search failed",
          err
        );

        toast.error(
          "Failed to fetch enquiry report"
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  /* =========================================================
     VIEW ALL
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewAll =
    async () => {
      try {
        setLoading(
          true
        );

        const res =
          await axios.get(
            `${API_BASE}/getenquirydetails_rpt`,
            {
              params: {
                branchid:
                  branchId ||
                  "8",

                branchwiseno:
                  "",

                fromdate:
                  "",

                todate:
                  "",
              },

              headers:
                commonHeaders,
            }
          );

        const data =
          Array.isArray(
            res.data?.data
          )
            ? res.data.data
            : [];

        setResults(
          data
        );

        toast.success(
          "All records loaded"
        );
      } catch (
        err
      ) {
        console.error(
          "View all failed",
          err
        );

        toast.error(
          "Failed to fetch all enquiry records"
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            PURCHASE REQUISITION ENQUIRY REPORT
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT SIDE */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaSearch className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Purchase Requisition Enquiry
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Search and review purchase requisition enquiry records
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER TOGGLE
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER BUTTON */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ENQUIRY NUMBER */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Enquiry No
                  </label>

                  <input
                    type="text"
                    value={
                      reqNo
                    }
                    onChange={(e) =>
                      setReqNo(
                        e.target.value
                      )
                    }
                    placeholder="Enter enquiry number"
                    className={
                      inputClass
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      store
                    }
                    onChange={(e) =>
                      setStore(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {storeOptions.map(
                      (s) => (
                        <option
                          key={
                            s.fld_storeid
                          }
                          value={
                            s.fld_storeid
                          }
                        >
                          {
                            s.fld_storename
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION ROW
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR FILTERS */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* VIEW ALL */}

                <button
                  type="button"
                  onClick={
                    handleViewAll
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition disabled:opacity-50 ${
                    isDark
                      ? "bg-slate-700 text-slate-200 hover:bg-slate-600"
                      : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                  }`}
                >
                  <FaEye
                    size={14}
                  />

                  View All
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Enquiry Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {results.length}{" "}
                  {results.length === 1
                    ? "record"
                    : "records"}
                </span>
              </div>

              {results.length >
                0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading enquiry records...
              </p>
            </div>
          ) : results.length >
            0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* TABLE HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Sl No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Enquiry No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Branch Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier Name
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      View
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Print
                    </th>
                  </tr>
                </thead>

                {/* TABLE BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {results.map(
                    (
                      item,
                      idx
                    ) => (
                      <tr
                        key={
                          item.fld_enquiryid ||
                          item.fld_purchaseenquiryid ||
                          idx
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SERIAL NO */}

                        <td className="px-4 py-3 font-medium">
                          {idx + 1}
                        </td>

                        {/* ENQUIRY NO */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_enquiryno ||
                              "-"}
                          </span>
                        </td>

                        {/* DATE */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(
                            item.fld_date
                          )}
                        </td>

                        {/* BRANCH */}

                        <td className="px-4 py-3 font-medium">
                          {item.fld_branchname ||
                            "-"}
                        </td>

                        {/* SUPPLIER */}

                        <td className="px-4 py-3">
                          {item.fld_suppliername ||
                            "-"}
                        </td>

                        {/* VIEW */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handleView(
                                item
                              )
                            }
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View Details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>

                        {/* PRINT */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handlePrint(
                                item
                              )
                            }
                            className={`p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                            title="Print Enquiry"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBoxOpen
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No enquiry records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default PurchaseReqEnquiryReport;